// Field-label groups for the three CumulativePeriodSummary sub-objects (BE9) — keys mirror
// the exact JSON property names returned by CalculateSelfEmploymentCumulativePeriodSummary
// (see the ITSA frontend guide, section 4 / types.ts), used to build FiguresReviewTable rows.
export type FigureField = { key: string; label: string }

export const INCOME_FIELDS: FigureField[] = [
  { key: 'turnover', label: 'Turnover' },
  { key: 'other', label: 'Other income' },
  { key: 'taxTakenOffTradingIncome', label: 'Tax taken off trading income' }
]

export const EXPENSE_FIELDS: FigureField[] = [
  { key: 'costOfGoods', label: 'Cost of goods' },
  { key: 'paymentsToSubcontractors', label: 'Payments to subcontractors' },
  { key: 'wagesAndStaffCosts', label: 'Wages and staff costs' },
  { key: 'carVanTravelExpenses', label: 'Car, van and travel expenses' },
  { key: 'premisesRunningCosts', label: 'Premises running costs' },
  { key: 'maintenanceCosts', label: 'Maintenance costs' },
  { key: 'adminCosts', label: 'Admin costs' },
  { key: 'businessEntertainmentCosts', label: 'Business entertainment costs' },
  { key: 'advertisingCosts', label: 'Advertising costs' },
  { key: 'interestOnBankOtherLoans', label: 'Interest on bank and other loans' },
  { key: 'financeCharges', label: 'Finance charges' },
  { key: 'irrecoverableDebts', label: 'Irrecoverable debts' },
  { key: 'professionalFees', label: 'Professional fees' },
  { key: 'depreciation', label: 'Depreciation' },
  { key: 'otherExpenses', label: 'Other expenses' }
]

// The disallowable set is the same fifteen categories, each with a `Disallowable` suffix on
// the key (mirrors Osprey.Accounts.BusinessLogic.Abstractions.Models
// .CumulativePeriodDisallowableExpenses exactly) — derived rather than hand-duplicated so the
// two lists can never drift apart.
export const DISALLOWABLE_EXPENSE_FIELDS: FigureField[] = EXPENSE_FIELDS.map(({ key, label }) => ({
  key: `${key}Disallowable`,
  label: `${label} (disallowable)`
}))

export const OBLIGATION_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Open', label: 'Open' },
  { value: 'Fulfilled', label: 'Fulfilled' }
] as const

// The HMRC OAuth setup screen (FE5) — same path SetupModule.tsx itself expects
// (location.state.tokenExpired), mirroring ReportModule's reconnect-prompt navigation.
export const HMRC_SETUP_ROUTE_PATH = '/supervisor/financial-setup/hmrc-itsa-setup'

// Extracts the server-supplied message from an axios error thrown by ospreyAPI.
// OspreyExceptionMiddleware (every Osprey .NET API) returns `{ StatusCode, Message }` on a
// non-2xx response — `message` (lowercase) is also checked defensively, mirroring the
// established defensive-extraction pattern elsewhere in the app (see
// modules/document-bundles CreateDocumentBundles.tsx).
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { Message?: string; message?: string } } })?.response
  return response?.data?.Message || response?.data?.message || (error as Error)?.message || fallback
}

// True when the API rejected the call with a 400 (ValidationException — e.g. the BE4
// ItsaFirmEligibilityGuard) as opposed to a 498 (HmrcTokenExpiredError, handled separately)
// or any other status.
export const isValidationError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 400

export const buildFigureRows = (fields: FigureField[], values: Record<string, number> | undefined | null) =>
  fields.map(({ key, label }) => ({ category: label, amount: values?.[key] ?? 0 }))
